import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

const Section = styled.section<{ $hasBackground: boolean; $backgroundSize?: string }>`
  position: absolute;
  inset: 0;
  background-size: ${({ $backgroundSize }) => $backgroundSize || 'cover'};
  background-position: center;
  background-attachment: fixed;
  z-index: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    background-attachment: scroll;
  }
`;

const Overlay = styled.div<{ $customOverlay?: string; $opacity: number }>`
  position: absolute;
  inset: 0;
  background: ${({ $customOverlay }) =>
    $customOverlay || 'linear-gradient(135deg, rgba(7,16,36,0.85) 0%, rgba(11,18,32,0.75) 100%)'};
  backdrop-filter: blur(1px);
  z-index: ${({ theme }) => theme.zIndex.background};
  opacity: ${({ $opacity }) => $opacity};
`;

interface BackgroundSectionProps {
  backgroundImage?: string;
  children?: React.ReactNode;
  className?: string;
  opacity?: number;
  overlayStyle?: string;
  backgroundSize?: string;
}

const BackgroundSection: React.FC<BackgroundSectionProps> = ({
  backgroundImage,
  children,
  className,
  opacity = 0.8,
  overlayStyle,
  backgroundSize
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  useEffect(() => {
    if (!backgroundImage) return;

    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageLoaded(false);
    img.src = backgroundImage;
  }, [backgroundImage]);

  const sectionStyle = imageLoaded && backgroundImage
    ? { 
        backgroundImage: `url(${backgroundImage})`,
        filter: 'brightness(1.2) contrast(1.1)' // Brighten the background image
      }
    : { background: 'linear-gradient(135deg, #0f1724 0%, #1e293b 100%)' };

  return (
    <Section
      className={className}
      style={sectionStyle}
      $hasBackground={imageLoaded && !!backgroundImage}
      $backgroundSize={backgroundSize}
    >
      <Overlay 
        $customOverlay={overlayStyle}
        $opacity={opacity}
      />
      {children}
    </Section>
  );
};

export default BackgroundSection;